import React, { useState, useEffect } from 'react';
import { toChileDate } from '../../utils/chileTime';
import { enrollmentsApi, trainingsApi, evaluationsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Chalkboard,
  Calendar,
  Clock,
  Link as LinkIcon,
  PlayCircle,
  CheckCircle,
  XCircle,
  ClipboardText
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export default function WorkerTrainingsPage() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await enrollmentsApi.getAll({ worker_id: user?.id });
      setEnrollments(res.data);
    } catch (error) {
      toast.error('Error al cargar capacitaciones');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (enrollment) => {
    try {
      const [trainingRes, evalsRes] = await Promise.all([
        trainingsApi.getById(enrollment.training_id),
        evaluationsApi.getByTraining(enrollment.training_id)
      ]);
      setSelectedTraining({ ...trainingRes.data, enrollment });
      setEvaluations(evalsRes.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const startEvaluation = (evaluationId) => {
    navigate(`/worker/evaluations?start=${evaluationId}`);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pendiente', className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
      confirmed: { label: 'Confirmada', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      attended: { label: 'Asistió', className: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
      completed: { label: 'Completada', className: 'bg-[#2BBAA4]/10 text-[#2BBAA4] border-[#2BBAA4]/20' },
      cancelled: { label: 'Cancelada', className: 'bg-red-500/10 text-red-400 border-red-500/20' },
    };
    const c = config[status] || config.pending;
    return <Badge className={`${c.className} border`}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="worker-trainings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Mis Capacitaciones</h1>
        <p className="text-[#2B4B8C]/60">Capacitaciones en las que estás inscrito</p>
      </div>

      {/* Trainings Grid */}
      <div className="grid gap-4">
        {enrollments.length > 0 ? enrollments.map((enrollment) => (
          <Card 
            key={enrollment.id} 
            className="bg-white border-[#2B4B8C]/10 hover:border-[#2BBAA4]/30 transition-colors cursor-pointer"
            onClick={() => openDetail(enrollment)}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-[#2B4B8C]/20 flex items-center justify-center flex-shrink-0">
                    <Chalkboard size={28} className="text-[#2B4B8C]" />
                  </div>
                  <div>
                    <h3 className="text-[#2B4B8C] font-semibold text-lg">{enrollment.training_name}</h3>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#2B4B8C]/60">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {enrollment.training_scheduled_date && format(toChileDate(enrollment.training_scheduled_date), "dd MMM yyyy HH:mm", { locale: es })}
                      </span>
                      {enrollment.training_modality && (
                        <span>{enrollment.training_modality}</span>
                      )}
                      {enrollment.training_type && (
                        <Badge className="bg-[#2B4B8C]/10 text-[#2B4B8C] border-[#2B4B8C]/20 border text-xs">
                          {enrollment.training_type}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(enrollment.status)}
                  {enrollment.training_status && (
                    <Badge className={
                      enrollment.training_status === 'realizada'
                        ? 'bg-[#2BBAA4]/10 text-[#2BBAA4] border-[#2BBAA4]/20 border text-xs'
                        : enrollment.training_status === 'conformada'
                        ? 'bg-blue-500/10 text-blue-500 border-blue-500/20 border text-xs'
                        : 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20 border text-xs'
                    }>
                      {enrollment.training_status === 'realizada' ? 'Realizada' : enrollment.training_status === 'conformada' ? 'Conformada' : 'Sin Conformar'}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="py-12 text-center text-[#2B4B8C]/50">
              No estás inscrito en ninguna capacitación
            </CardContent>
          </Card>
        )}
      </div>

      {/* Training Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-3">
              <Chalkboard size={24} className="text-[#2BBAA4]" />
              {selectedTraining?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTraining && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="bg-[#2B4B8C]/5">
                <TabsTrigger value="info">Información</TabsTrigger>
                <TabsTrigger value="evaluation">Evaluación</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-[#f8fafc]">
                    <p className="text-sm text-[#2B4B8C]/60">Tipo</p>
                    <p className="text-[#2B4B8C]">{selectedTraining.training_type}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#f8fafc]">
                    <p className="text-sm text-[#2B4B8C]/60">Modalidad</p>
                    <p className="text-[#2B4B8C]">{selectedTraining.modality}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#f8fafc]">
                    <p className="text-sm text-[#2B4B8C]/60">Fecha</p>
                    <p className="text-[#2B4B8C]">
                      {format(toChileDate(selectedTraining.scheduled_date), "dd/MM/yyyy HH:mm")}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#f8fafc]">
                    <p className="text-sm text-[#2B4B8C]/60">Duración</p>
                    <p className="text-[#2B4B8C]">{selectedTraining.duration_minutes} minutos</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#f8fafc]">
                    <p className="text-sm text-[#2B4B8C]/60">% Aprobación</p>
                    <p className="text-[#2B4B8C]">{selectedTraining.passing_percentage}%</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#f8fafc]">
                    <p className="text-sm text-[#2B4B8C]/60">Intentos Permitidos</p>
                    <p className="text-[#2B4B8C]">{selectedTraining.max_evaluation_attempts}</p>
                  </div>
                </div>

                {selectedTraining.connection_url && (
                  <div className="p-4 rounded-lg bg-[#2B4B8C]/10 border border-[#2B4B8C]/20">
                    <p className="text-sm text-[#2B4B8C]/60 mb-2 flex items-center gap-2">
                      <LinkIcon size={16} />
                      Enlace de Conexión
                    </p>
                    <a 
                      href={selectedTraining.connection_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-[#2BBAA4] hover:underline break-all"
                    >
                      {selectedTraining.connection_url}
                    </a>
                  </div>
                )}

                {selectedTraining.description && (
                  <div className="p-4 rounded-lg bg-[#f8fafc]">
                    <p className="text-sm text-[#2B4B8C]/60 mb-2">Descripción</p>
                    <p className="text-[#2B4B8C]">{selectedTraining.description}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="evaluation" className="mt-4">
                {evaluations.length > 0 ? (
                  <div className="space-y-4">
                    {evaluations.map((evaluation) => (
                      <Card key={evaluation.id} className="bg-[#f8fafc] border-[#2B4B8C]/5">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-[#2B4B8C] font-medium">{evaluation.name}</h4>
                              <div className="flex items-center gap-4 mt-2 text-sm text-[#2B4B8C]/60">
                                <span className="flex items-center gap-1">
                                  <Clock size={14} />
                                  {evaluation.time_limit_minutes} min
                                </span>
                                <span>{evaluation.questions_count} preguntas</span>
                                <span>{evaluation.passing_percentage}% para aprobar</span>
                              </div>
                            </div>
                            {selectedTraining.status === 'realizada' && selectedTraining.enrollment?.status !== 'approved' ? (
                              <Button 
                                onClick={() => startEvaluation(evaluation.id)}
                                className="bg-[#2BBAA4] text-black hover:bg-[#239986]"
                                data-testid="start-evaluation-btn"
                              >
                                <PlayCircle className="mr-2" size={18} />
                                Iniciar Evaluación
                              </Button>
                            ) : selectedTraining.enrollment?.status === 'approved' ? (
                              <Badge className="bg-green-500/10 text-green-500 border-green-500/20 border">
                                <CheckCircle size={12} className="mr-1" />
                                Aprobado por Admin
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-500/10 text-[#2B4B8C]/60 border-gray-500/20 border">
                                Capacitación no realizada
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#2B4B8C]/50">
                    No hay evaluaciones configuradas
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
